/**
 * Top-of-page progress indicator for the booking wizard.
 *
 * On wide screens we show numbered circles + labels for all 6 steps. On
 * narrow screens we collapse to a thin bar + "Step N / 6" label so the
 * wizard stays usable on phones without horizontal scroll.
 */

import { useTranslation } from "react-i18next";

const STEP_KEYS = [
  "industry",
  "organization",
  "service",
  "datetime",
  "confirm",
];

export function BookingProgress({
  palette,
  current,
  completed = [],
  onStepClick,
  steps = STEP_KEYS,
}) {
  const { t } = useTranslation();
  const total = steps.length;
  const progressPct =
    total > 1 ? Math.round(((current + 0) / (total - 1)) * 100) : 100;

  return (
    <div className="w-100">
      {/* Desktop / tablet view */}
      <div className="d-none d-md-block">
        <div className="d-flex align-items-center justify-content-between position-relative">
          {/* Track behind */}
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              left: 18,
              right: 18,
              top: 18,
              height: 4,
              background: palette.progressTrack,
              borderRadius: 999,
              zIndex: 0,
            }}
          />
          {/* Filled portion */}
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              left: 18,
              top: 18,
              width: `calc(${progressPct}% - 18px)`,
              height: 4,
              background: palette.progressBar,
              borderRadius: 999,
              transition: "width 0.3s ease",
              zIndex: 1,
            }}
          />
          {steps.map((key, idx) => {
            const isDone = completed.includes(idx) && idx < current;
            const isActive = idx === current;
            const canClick = typeof onStepClick === "function" && (isDone || isActive);
            const bg = isActive
              ? palette.stepActiveBg
              : isDone
              ? palette.stepDoneBg
              : palette.stepIdleBg;
            const fg = isActive
              ? palette.stepActiveText
              : isDone
              ? palette.stepDoneText
              : palette.stepIdleText;
            const labelColor = isActive
              ? palette.stepLabelActive
              : palette.stepLabelIdle;
            return (
              <button
                key={key}
                type="button"
                onClick={canClick ? () => onStepClick(idx) : undefined}
                disabled={!canClick}
                className="d-flex flex-column align-items-center"
                style={{
                  background: "transparent",
                  border: "none",
                  padding: 0,
                  position: "relative",
                  zIndex: 2,
                  cursor: canClick ? "pointer" : "default",
                  flex: 1,
                  minWidth: 0,
                }}
                aria-current={isActive ? "step" : undefined}
              >
                <span
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: "50%",
                    background: bg,
                    color: fg,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 700,
                    fontSize: "0.95rem",
                    boxShadow: isActive ? palette.shadowSoft : "none",
                    border: isActive
                      ? `2px solid ${palette.accent}`
                      : "2px solid transparent",
                    transition: "all 0.2s ease",
                  }}
                >
                  {isDone ? <i className="las la-check" /> : idx + 1}
                </span>
                <span
                  className="text-truncate"
                  style={{
                    marginTop: 6,
                    fontSize: "0.78rem",
                    fontWeight: isActive ? 600 : 500,
                    color: labelColor,
                    maxWidth: "100%",
                    padding: "0 4px",
                  }}
                >
                  {t(`booking.steps.${key}`)}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Mobile view */}
      <div className="d-block d-md-none">
        <div className="d-flex align-items-center justify-content-between gap-2 mb-1">
          <span
            style={{
              fontSize: "0.7rem",
              fontWeight: 700,
              color: palette.accent,
              background: palette.accentSoft,
              borderRadius: 999,
              padding: "3px 8px",
              whiteSpace: "nowrap",
            }}
          >
            {current + 1}/{total}
          </span>
          <span
            className="text-truncate"
            style={{
              fontSize: "0.88rem",
              fontWeight: 800,
              color: palette.text,
              minWidth: 0,
            }}
          >
            {t(`booking.steps.${steps[current]}`)}
          </span>
        </div>
        <div
          style={{
            width: "100%",
            height: 5,
            background: palette.progressTrack,
            borderRadius: 999,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              width: `${progressPct}%`,
              height: "100%",
              background: palette.progressBar,
              transition: "width 0.3s ease",
            }}
          />
        </div>
      </div>
    </div>
  );
}

export const BOOKING_STEP_KEYS = STEP_KEYS;
